import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/axiosConfig";
import "../styles/pages/ResetPassword.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    emailOrPhone: location.state?.email || "",
    otp: "",
    newPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleResendOTP = async () => {
    if (!form.emailOrPhone) {
      setError("Please enter your registered email address first.");
      return;
    }
    if (resendTimer > 0 || resendLoading) return;

    setError("");
    setMsg("");
    setResendLoading(true);

    try {
      await api.post("/auth/send-reset-otp", { emailOrPhone: form.emailOrPhone });
      setMsg("A fresh OTP has been sent to your email!");
      setResendTimer(45); // 45 seconds cooldown
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", form);
      setMsg("Password changed! You can now log in.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(err.response?.data?.message || "Problem resetting password!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp_page_92831">

      {/* 🔒 GLOBAL LOGO — ALWAYS VISIBLE */}
      <div className="rp_globalLogo_92831">
        <a href="https://Fotographiya.com" target="_blank" rel="noreferrer">
          <img src="/images/logo_colored_white.png" alt="Fotographiya" width="1299" height="192" />
        </a>
      </div>

      <div className="rp_container_92831">

        {/* LEFT IMAGE (DESKTOP ONLY) */}
        <div className="rp_left_92831">
          <div className="rp_imageWrap_92831">
            <img
              src="/images/login-banner.jpg"
              alt="banner"
              className="rp_bannerImg_92831"
            />

            <div className="rp_overlayText_92831">
              <p>"A new password brings new confidence — return with security."</p>
              <h4>Team Fotographiya</h4>
              <p style={{ fontSize: "12px" }}>Technical Support</p>
            </div>
          </div>
        </div>

        {/* RIGHT FORM */}
        <div className="rp_right_92831">
          <div className="rp_formWrap_92831">
            <div className="rp_formTop_92831">
              <h2>🔒 Reset Password</h2>
              <span>Enter OTP and new password</span>

              <form onSubmit={handleSubmit} autoComplete="off">
                <label style={{ width: "100%" }} htmlFor="reset-emailOrPhone">
                  <span className="sr-only">Email Address</span>
                  <input
                    id="reset-emailOrPhone"
                    className="rp_input_92831"
                    type="email"
                    name="emailOrPhone"
                    placeholder="Your registered email address"
                    value={form.emailOrPhone}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                  />
                </label>

                <label style={{ width: "100%" }} htmlFor="reset-otp">
                  <span className="sr-only">6-digit OTP</span>
                  <input
                    id="reset-otp"
                    className="rp_input_92831"
                    type="text"
                    name="otp"
                    placeholder="6-digit OTP"
                    value={form.otp}
                    onChange={handleChange}
                    autoComplete="one-time-code"
                    required
                  />
                </label>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", marginBottom: "4px", padding: "0 4px" }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Didn't get OTP or expired?</span>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0 || resendLoading}
                    style={{
                      background: "none",
                      border: "none",
                      color: resendTimer > 0 ? "#94a3b8" : "#7c3aed",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                      padding: "4px 6px",
                      textDecoration: resendTimer > 0 ? "none" : "underline",
                      transition: "color 0.2s"
                    }}
                  >
                    {resendLoading ? "Sending..." : resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : "Resend OTP"}
                  </button>
                </div>

                <div className="rp_password_wrapper_92831">
                  <label style={{ width: "100%", display: "block" }} htmlFor="reset-newPassword">
                    <span className="sr-only">New Password</span>
                    <input
                      id="reset-newPassword"
                      className="rp_input_92831"
                      type={showPassword ? "text" : "password"}
                      name="newPassword"
                      placeholder="New password"
                      value={form.newPassword}
                      onChange={handleChange}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                  <button
                    type="button"
                    className="rp_password_toggle_btn_92831"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>

                <button className="btn btn-primary-gradient login-btn" type="submit" disabled={loading}>
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>

              {msg && <p className="rp_success_92831">{msg}</p>}
              {error && <p className="rp_error_92831">{error}</p>}

              <p className="rp_footer_92831">
                Back to <a href="/login">Login</a>
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ResetPassword;
